import React from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'

export type ScoreFactor = {
  name: string
  points: number
  maxPoints: number
  note?: string | null
  count?: number | null
}

const SCORE_HEADER_COLOR = '#99A1B7'
const ROSETTE_FILL = '#a3a242'

const formatPoints = (value: number) => {
  if (value === 0) return '0'
  return (Math.round(value * 100) / 100).toString()
}

export const ScoreRosetteIcon: React.FC<{size?: number}> = ({size = 22}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 22 22'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden
  >
    <circle cx='11' cy='11' r='11' fill={ROSETTE_FILL} />
    <path
      d='M11 5.5L12.35 9.65L16.75 9.65L13.2 12.25L14.55 16.5L11 13.85L7.45 16.5L8.8 12.25L5.25 9.65L9.65 9.65L11 5.5Z'
      fill='white'
    />
    <circle cx='11' cy='4.2' r='0.85' fill='white' />
    <circle cx='11' cy='17.1' r='0.85' fill='white' />
  </svg>
)

export const ScoreColumnHeader: React.FC = () => (
  <span
    className='d-inline-flex align-items-center text-uppercase fw-semibold'
    style={{gap: 6, color: SCORE_HEADER_COLOR, fontSize: 'inherit'}}
  >
    <i className='bi bi-trophy' style={{fontSize: '1rem', color: SCORE_HEADER_COLOR}} />
    <span>Score</span>
  </span>
)

type Props = {
  score?: number | null
  maxScore?: number
  breakdown?: ScoreFactor[] | null
  isNew?: boolean
  placement?: 'left' | 'right' | 'top' | 'bottom'
}

export const InterpreterScoreBadge: React.FC<Props> = ({
  score,
  breakdown,
  isNew,
  placement = 'left',
}) => {
  if (isNew) {
    return null
  }

  if (score === undefined || score === null) {
    return <span className='text-muted'>N/A</span>
  }

  const display = formatPoints(score)

  const tooltip = (
    <Tooltip id='interpreter-score-tooltip' style={{maxWidth: 320}}>
      <div className='d-flex flex-column text-start' style={{gap: 4, padding: '4px 2px'}}>
        {breakdown && breakdown.length > 0 ? (
          breakdown.map((f, idx) => {
            const label =
              typeof f.count === 'number' && f.count > 0
                ? `${f.name}(${f.count})`
                : f.name
            const value = f.note ? f.note : formatPoints(f.points)
            return (
              <div
                key={`${f.name}-${idx}`}
                className='small d-flex justify-content-between align-items-center'
                style={{gap: 16}}
              >
                <span className='text-start'>{label}</span>
                <span className='fw-semibold ms-3'>{value}</span>
              </div>
            )
          })
        ) : (
          <div className='small'>No factor data available</div>
        )}
        <div
          className='border-top mt-1 pt-1 small fw-bold d-flex justify-content-between align-items-center'
          style={{gap: 16}}
        >
          <span>Total Score</span>
          <span>{formatPoints(score)}</span>
        </div>
      </div>
    </Tooltip>
  )

  return (
    <OverlayTrigger placement={placement} overlay={tooltip}>
      <span
        className='d-inline-flex align-items-center fw-bold'
        style={{cursor: 'help', gap: 8, fontSize: '0.95rem', color: '#000000'}}
      >
        <ScoreRosetteIcon size={22} />
        <span style={{color: '#000000'}}>{display}</span>
      </span>
    </OverlayTrigger>
  )
}

type TagProps = {
  isNew?: boolean
  isTopMatch?: boolean
}

export const InterpreterTag: React.FC<TagProps> = ({isNew, isTopMatch}) => {
  if (isNew) {
    return (
      <span
        className='badge badge-light-info fw-semibold mt-1'
        style={{
          padding: '4px 10px',
          borderRadius: '999px',
          fontSize: '0.7rem',
          width: 'fit-content',
          display: 'inline-flex',
          alignSelf: 'flex-start',
        }}
      >
        New
      </span>
    )
  }

  if (isTopMatch) {
    return (
      <span
        className='badge fw-semibold mt-1'
        style={{
          padding: '4px 10px',
          borderRadius: '999px',
          fontSize: '0.7rem',
          width: 'fit-content',
          display: 'inline-flex',
          alignSelf: 'flex-start',
          color: '#a3a242',
          backgroundColor: 'rgba(163, 162, 66, 0.14)',
        }}
      >
        Top Match
      </span>
    )
  }

  return null
}

type AiTagProps = {
  label: string
  title?: string
}

export const AiRecommendedTag: React.FC<AiTagProps> = ({label, title}) => (
  <span
    className='badge fw-semibold mt-1'
    style={{
      padding: '4px 10px',
      borderRadius: '999px',
      fontSize: '0.7rem',
      width: 'fit-content',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      alignSelf: 'flex-start',
      color: '#a3a242',
      backgroundColor: 'rgba(163, 162, 66, 0.14)',
    }}
    title={title}
  >
    <i className='bi bi-robot' style={{fontSize: '0.7rem', color: 'inherit'}} aria-hidden='true' />
    {label}
  </span>
)

export default InterpreterScoreBadge
